/**
 * @fileoverview Boter Kaas en Eieren Index Screen
 * @description Game lobby where users can start games, see active games, and view stats
 */

import React, { useState, useCallback, useEffect } from "react";
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  ActivityIndicator,
  RefreshControl,
  Modal,
  FlatList,
  Image,
  Alert,
  TextInput,
  KeyboardAvoidingView,
  Platform,
} from "react-native";
import { SafeAreaView } from "react-native-safe-area-context";
import { router, useLocalSearchParams } from "expo-router";
import { Ionicons } from "@expo/vector-icons";
import { useTheme, useThemeColors } from "@/providers/ThemeProvider";
import { useAuth } from "@/providers/AuthProvider";
import { supabase } from "@/lib/supabase/client";
import { GameModeSelector } from "@/modules/games";

type SessionMode = "free" | "weekend" | "practice";
type SessionType = "solo" | "versus";

interface FamilyMember {
  id: string;
  full_name: string;
  avatar_url: string | null;
  team_id?: string | null;
  team_name?: string | null;
}

type SeriesType = "single" | "best_of_3" | "best_of_5";

interface LeaderboardEntry {
  rank: number;
  user_id: string;
  full_name: string;
  avatar_url: string | null;
  games_won: number;
  games_played: number;
  total_points: number;
}

/**
 * TicTacToeIndexScreen - Game lobby
 * Uses activeProfile to support guardian mode - when a guardian switches to
 * a dependent, games are created for that dependent.
 */
export default function TicTacToeIndexScreen() {
  const { isDark } = useTheme();
  const colors = useThemeColors();
  const { user, activeProfile, activeWeekend, userTeam } = useAuth();
  
  // Use activeProfile for game actions (supports guardian mode)
  // The profile used for game creation and stats
  const targetUserId = activeProfile?.id;
  const targetProfile = activeProfile;
  const { mode: urlMode } = useLocalSearchParams<{ mode?: string }>();

  const [isLoading, setIsLoading] = useState(false);
  const [selectedMode, setSelectedMode] = useState<SessionMode>(
    urlMode === "weekend" ? "weekend" : "free"
  );
  const [refreshing, setRefreshing] = useState(false);
  const [recentGames, setRecentGames] = useState<unknown[]>([]);
  const [showChallengeModal, setShowChallengeModal] = useState(false);
  const [familyMembers, setFamilyMembers] = useState<FamilyMember[]>([]);
  const [loadingMembers, setLoadingMembers] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [gameEnabledForWeekend, setGameEnabledForWeekend] = useState(false);

  // Filter family members by search query
  const filteredMembers = familyMembers.filter((member) =>
    member.full_name?.toLowerCase().includes(searchQuery.toLowerCase())
  );
  const [selectedSeries, setSelectedSeries] = useState<SeriesType>("single");
  const [leaderboard, setLeaderboard] = useState<LeaderboardEntry[]>([]);
  const [weekendPoints, setWeekendPoints] = useState<number | null>(null);
  const [stats, setStats] = useState({
    wins: 0,
    losses: 0,
    draws: 0,
    total: 0,
  });

  // Determine if weekend mode is available
  const isWeekendModeAvailable = !!activeWeekend && !!userTeam && gameEnabledForWeekend;
  const weekendUnavailableReason = !activeWeekend
    ? "Geen actief weekend"
    : !userTeam
    ? "Je zit nog niet in een team"
    : !gameEnabledForWeekend
    ? "Niet geactiveerd voor dit weekend"
    : "";

  /**
   * Fetch weekend game config to check if game is enabled and get points
   */
  const fetchWeekendGameConfig = useCallback(async () => {
    if (!activeWeekend) {
      setGameEnabledForWeekend(false);
      setWeekendPoints(null);
      return;
    }

    try {
      const { data } = await supabase
        .from("weekend_game_config")
        .select("is_enabled, points_config")
        .eq("weekend_id", activeWeekend.id)
        .eq("game_slug", "tic_tac_toe")
        .single();

      setGameEnabledForWeekend(data?.is_enabled ?? false);
      const winPoints = (data?.points_config as { win?: number })?.win;
      setWeekendPoints(winPoints ?? null);
    } catch {
      setGameEnabledForWeekend(false);
      setWeekendPoints(null);
    }
  }, [activeWeekend]);

  /**
   * Fetch user's tic-tac-toe stats (for active profile)
   */
  const fetchStats = useCallback(async () => {
    if (!targetUserId) return;

    try {
      const { data } = await supabase
        .from("user_game_stats")
        .select("*")
        .eq("user_id", targetUserId)
        .eq("game_slug", "tic_tac_toe")
        .single();

      if (data) {
        setStats({
          wins: data.games_won ?? 0,
          losses: data.games_lost ?? 0,
          draws: data.games_drawn ?? 0,
          total: data.games_played ?? 0,
        });
      }
    } catch (err) {
      // No stats yet, that's fine
    }
  }, [targetUserId]);

  /**
   * Fetch game leaderboard
   */
  const fetchLeaderboard = useCallback(async () => {
    try {
      const { data, error } = await supabase.rpc("get_game_leaderboard", {
        p_game_slug: "tic_tac_toe",
        p_limit: 10,
      });

      if (error) throw error;
      setLeaderboard(data ?? []);
    } catch (err) {
      console.error("Error fetching leaderboard:", err);
    }
  }, []);

  /**
   * Handle refresh
   */
  const handleRefresh = useCallback(async () => {
    setRefreshing(true);
    await Promise.all([fetchStats(), fetchLeaderboard(), fetchWeekendGameConfig()]);
    setRefreshing(false);
  }, [fetchStats, fetchLeaderboard, fetchWeekendGameConfig]);

  useEffect(() => {
    fetchStats();
    fetchLeaderboard();
    fetchWeekendGameConfig();
  }, [fetchStats, fetchLeaderboard, fetchWeekendGameConfig]);

  /**
   * Start a new game (for active profile)
   */
  const handleStartGame = async (type: SessionType) => {
    if (!targetUserId) return;

    setIsLoading(true);

    try {
      // Create a new game session with correct schema
      // Initialize complete game state for solo mode
      const initialGameState = type === "solo" ? {
        board: [null, null, null, null, null, null, null, null, null],
        current_turn: "X" as const,
        status: "playing" as const,
        winner: null,
        win_line: null,
        move_count: 0,
        last_move_at: null,
        players: {
          X: targetUserId,
          O: "ai",
        },
        move_history: [],
        ai_difficulty: "medium",
      } : null; // Versus games initialize state when both players are ready

      const { data: session, error } = await supabase
        .from("game_sessions")
        .insert({
          game_slug: "tic_tac_toe",
          created_by: targetUserId,
          participants: type === "solo" 
            ? [{ user_id: targetUserId, role: "player", symbol: "X" }]
            : [{ user_id: targetUserId, role: "player", symbol: "X" }],
          mode: selectedMode,
          session_type: type,
          status: type === "solo" ? "active" : "waiting",
          game_state: initialGameState,
        })
        .select()
        .single();

      if (error) throw error;

      // Navigate to the game
      router.push(`/(tabs)/games/tic-tac-toe/${session.id}` as any);
    } catch (err) {
      console.error("Error starting game:", err);
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Fetch family members for challenge (for active profile)
   * For weekend mode, only show members from different teams
   */
  const fetchFamilyMembers = useCallback(async () => {
    if (!targetUserId) return;

    setLoadingMembers(true);
    try {
      // Fetch all profiles with their team info (excluding active profile)
      const { data: profiles, error: profilesError } = await supabase
        .from("profiles")
        .select("id, full_name, avatar_url")
        .neq("id", targetUserId)
        .order("full_name");

      if (profilesError) throw profilesError;

      // If weekend mode, get team memberships
      if (selectedMode === "weekend" && activeWeekend) {
        const { data: teamMembers } = await supabase
          .from("team_members")
          .select("user_id, team:teams(id, name)")
          .eq("teams.weekend_id", activeWeekend.id);

        // Map team info to profiles and filter out same team
        const membersWithTeams: FamilyMember[] = (profiles || []).map((p) => {
          const membership = teamMembers?.find((tm) => tm.user_id === p.id);
          // Handle potential array or object from supabase join
          const teamRaw = membership?.team;
          const teamData = Array.isArray(teamRaw) ? teamRaw[0] : teamRaw;
          return {
            ...p,
            team_id: teamData?.id || null,
            team_name: teamData?.name || null,
          };
        });

        // Filter to only show members from different teams (for weekend challenges)
        const eligibleMembers = membersWithTeams.filter(
          (m) => m.team_id && m.team_id !== userTeam?.id
        );
        setFamilyMembers(eligibleMembers);
      } else {
        // Free play - show everyone
        setFamilyMembers(profiles || []);
      }
    } catch (err) {
      console.error("Error fetching family members:", err);
    } finally {
      setLoadingMembers(false);
    }
  }, [targetUserId, selectedMode, activeWeekend, userTeam?.id]);

  /**
   * Handle opening challenge modal
   */
  const handleChallenge = () => {
    setSearchQuery("");
    setShowChallengeModal(true);
    fetchFamilyMembers();
  };

  /**
   * Handle challenging a specific user (for active profile)
   */
  const handleChallengeUser = async (opponent: FamilyMember) => {
    if (!targetUserId || !targetProfile) return;

    setShowChallengeModal(false);
    setIsLoading(true);

    // Determine series settings
    const seriesConfig = {
      single: { games_to_win: 1, max_games: 1 },
      best_of_3: { games_to_win: 2, max_games: 3 },
      best_of_5: { games_to_win: 3, max_games: 5 },
    };
    const series = seriesConfig[selectedSeries];

    // Randomize who goes first (X always goes first)
    const challengerGoesFirst = Math.random() < 0.5;
    const challengerSymbol = challengerGoesFirst ? "X" : "O";
    const opponentSymbol = challengerGoesFirst ? "O" : "X";

    try {
      // Create a game session waiting for the opponent (for active profile)
      const { data: session, error } = await supabase
        .from("game_sessions")
        .insert({
          game_slug: "tic_tac_toe",
          created_by: targetUserId,
          participants: [
            { user_id: targetUserId, role: "player", symbol: challengerSymbol, name: targetProfile.full_name, team_id: userTeam?.id },
            { user_id: opponent.id, role: "player", symbol: opponentSymbol, name: opponent.full_name, team_id: opponent.team_id },
          ],
          mode: selectedMode,
          session_type: "versus",
          status: "waiting",
          game_state: {
            challenged_user_id: opponent.id,
            challenged_user_name: opponent.full_name,
            challenger_name: targetProfile.full_name,
            challenger_ready: false,
            challenged_ready: false,
            // Store initial players for game setup
            players: challengerGoesFirst 
              ? { X: targetUserId, O: opponent.id }
              : { X: opponent.id, O: targetUserId },
            // Series info
            series_type: selectedSeries,
            games_to_win: series.games_to_win,
            max_games: series.max_games,
            series_score: { [targetUserId]: 0, [opponent.id]: 0 },
            current_game: 1,
          },
          weekend_id: selectedMode === "weekend" ? activeWeekend?.id : null,
        })
        .select()
        .single();

      if (error) throw error;

      // Build notification message
      const seriesText = selectedSeries === "single" ? "" : 
        selectedSeries === "best_of_3" ? " (best of 3)" : " (best of 5)";

      // Create a notification for the opponent
      await supabase.from("notifications").insert({
        user_id: opponent.id,
        type: "game_challenge",
        notification_type: "game_challenge",
        title: `🎮 ${targetProfile.full_name} daagt je uit!`,
        body: `${targetProfile.full_name} wil een potje Boter Kaas en Eieren spelen${seriesText}. Accepteer je de uitdaging?`,
        action_url: `/(tabs)/games/tic-tac-toe/${session.id}`,
        action_data: {
          session_id: session.id,
          game_slug: "tic_tac_toe",
          challenger_id: targetUserId,
          challenger_name: targetProfile.full_name,
          series_type: selectedSeries,
        },
        sender_id: targetUserId,
        weekend_id: selectedMode === "weekend" ? activeWeekend?.id : null,
        lottie_animation: "game-controller",
        priority: "high",
      });

      // Navigate to waiting screen
      router.push(`/(tabs)/games/tic-tac-toe/${session.id}` as any);
    } catch (err) {
      console.error("Error creating challenge:", err);
      Alert.alert("Fout", "Kon uitdaging niet versturen. Probeer het opnieuw.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <SafeAreaView
      style={[styles.container, { backgroundColor: colors.background }]}
      edges={["top", "left", "right"]}
    >
      {/* Header */}
      <View style={styles.header}>
        <TouchableOpacity
          onPress={() => router.back()}
          style={styles.backButton}
          accessibilityLabel="Terug"
          accessibilityRole="button"
        >
          <Ionicons name="arrow-back" size={24} color={colors.text} />
        </TouchableOpacity>
        <View style={styles.headerContent}>
          <Text style={[styles.title, { color: colors.text }]}>
            ⭕ Boter Kaas en Eieren ❌
          </Text>
          <Text style={[styles.subtitle, { color: colors.textSecondary }]}>
            Klassiek boter-kaas-en-eieren
          </Text>
        </View>
      </View>

      <ScrollView
        style={styles.scrollView}
        contentContainerStyle={styles.content}
        refreshControl={
          <RefreshControl refreshing={refreshing} onRefresh={handleRefresh} />
        }
      >
        {/* Game Mode Selector */}
        <View style={styles.section}>
          <Text style={[styles.sectionTitle, { color: colors.text }]}>
            Spelmodus
          </Text>
          <GameModeSelector
            mode={selectedMode}
            onModeChange={setSelectedMode}
            weekendAvailable={isWeekendModeAvailable}
            weekendUnavailableReason={weekendUnavailableReason}
            isDark={isDark}
            compact
          />
        </View>

        {/* Game Options */}
        <View style={styles.section}>
          <Text style={[styles.sectionTitle, { color: colors.text }]}>
            Start een spel
          </Text>

          <View style={styles.gameOptions}>
            {/* Solo vs AI */}
            <TouchableOpacity
              style={[styles.gameCard, { backgroundColor: colors.surface }]}
              onPress={() => handleStartGame("solo")}
              disabled={isLoading}
              accessibilityLabel="Speel tegen AI"
              accessibilityRole="button"
            >
              <Ionicons name="hardware-chip" size={40} color="#8B5CF6" />
              <Text style={[styles.cardTitle, { color: colors.text }]}>
                Solo
              </Text>
              <Text
                style={[styles.cardSubtitle, { color: colors.textSecondary }]}
              >
                Speel tegen de AI
              </Text>
            </TouchableOpacity>

            {/* Challenge */}
            <TouchableOpacity
              style={[styles.gameCard, { backgroundColor: colors.surface }]}
              onPress={handleChallenge}
              disabled={isLoading}
              accessibilityLabel="Daag iemand uit"
              accessibilityRole="button"
            >
              <Ionicons name="people" size={40} color="#EF4444" />
              <Text style={[styles.cardTitle, { color: colors.text }]}>
                Uitdagen
              </Text>
              <Text
                style={[styles.cardSubtitle, { color: colors.textSecondary }]}
              >
                Daag een familielid uit
              </Text>
            </TouchableOpacity>
          </View>
        </View>

        {/* Stats */}
        <View style={styles.section}>
          <Text style={[styles.sectionTitle, { color: colors.text }]}>
            Jouw statistieken
          </Text>

          <View style={[styles.statsCard, { backgroundColor: colors.surface }]}>
            <View style={styles.statItem}>
              <Text style={styles.statValue}>{stats.total}</Text>
              <Text
                style={[styles.statLabel, { color: colors.textSecondary }]}
              >
                Gespeeld
              </Text>
            </View>
            <View style={styles.statDivider} />
            <View style={styles.statItem}>
              <Text style={[styles.statValue, { color: "#22C55E" }]}>
                {stats.wins}
              </Text>
              <Text
                style={[styles.statLabel, { color: colors.textSecondary }]}
              >
                Gewonnen
              </Text>
            </View>
            <View style={styles.statDivider} />
            <View style={styles.statItem}>
              <Text style={[styles.statValue, { color: "#EF4444" }]}>
                {stats.losses}
              </Text>
              <Text
                style={[styles.statLabel, { color: colors.textSecondary }]}
              >
                Verloren
              </Text>
            </View>
            <View style={styles.statDivider} />
            <View style={styles.statItem}>
              <Text style={[styles.statValue, { color: "#F59E0B" }]}>
                {stats.draws}
              </Text>
              <Text
                style={[styles.statLabel, { color: colors.textSecondary }]}
              >
                Gelijk
              </Text>
            </View>
          </View>
        </View>

        {/* Weekend Points Info */}
        {selectedMode === "weekend" && weekendPoints !== null && (
          <View style={styles.section}>
            <View style={[styles.weekendPointsCard, { backgroundColor: "#F59E0B" }]}>
              <Ionicons name="trophy" size={24} color="#FFFFFF" />
              <View style={styles.weekendPointsContent}>
                <Text style={styles.weekendPointsTitle}>Weekend Punten</Text>
                <Text style={styles.weekendPointsValue}>
                  {weekendPoints} punten bij winst
                </Text>
              </View>
            </View>
          </View>
        )}

        {/* Leaderboard */}
        {leaderboard.length > 0 && (
          <View style={styles.section}>
            <Text style={[styles.sectionTitle, { color: colors.text }]}>
              Ranglijst
            </Text>
            <View style={[styles.leaderboardCard, { backgroundColor: colors.surface }]}>
              {leaderboard.slice(0, 5).map((entry, index) => {
                const isCurrentUser = entry.user_id === targetUserId;
                return (
                  <TouchableOpacity
                    key={entry.user_id}
                    style={[
                      styles.leaderboardItem,
                      index > 0 && { borderTopWidth: 1, borderTopColor: colors.border },
                      isCurrentUser && { backgroundColor: colors.primaryLight },
                    ]}
                    onPress={() => router.push(`/(tabs)/profile/user/${entry.user_id}`)}
                  >
                    <View style={styles.leaderboardRank}>
                      {index === 0 ? (
                        <Text style={styles.rankEmoji}>🥇</Text>
                      ) : index === 1 ? (
                        <Text style={styles.rankEmoji}>🥈</Text>
                      ) : index === 2 ? (
                        <Text style={styles.rankEmoji}>🥉</Text>
                      ) : (
                        <Text style={[styles.rankNumber, { color: colors.textSecondary }]}>
                          {entry.rank}
                        </Text>
                      )}
                    </View>
                    {entry.avatar_url ? (
                      <Image
                        source={{ uri: entry.avatar_url }}
                        style={styles.leaderboardAvatar}
                      />
                    ) : (
                      <View style={[styles.leaderboardAvatarPlaceholder, { backgroundColor: colors.primaryLight }]}>
                        <Text style={[styles.avatarText, { color: colors.primary }]}>
                          {entry.full_name?.charAt(0) || "?"}
                        </Text>
                      </View>
                    )}
                    <View style={styles.leaderboardInfo}>
                      <Text style={[styles.leaderboardName, { color: colors.text }]} numberOfLines={1}>
                        {entry.full_name}
                        {isCurrentUser && (
                          <Text style={[styles.youBadge, { color: colors.primary }]}> Jij</Text>
                        )}
                      </Text>
                      <Text style={[styles.leaderboardStats, { color: colors.textSecondary }]}>
                        {entry.games_won}W / {entry.games_played}G
                      </Text>
                    </View>
                    <Text style={[styles.leaderboardPoints, { color: colors.primary }]}>
                      {entry.total_points}
                    </Text>
                  </TouchableOpacity>
                );
              })}
              
              {/* Scoreboard link */}
              <TouchableOpacity
                style={[styles.scoreboardLink, { borderTopWidth: 1, borderTopColor: colors.border }]}
                onPress={() => router.push("/scores")}
              >
                <View style={[styles.scoreboardIcon, { backgroundColor: colors.primaryLight }]}>
                  <Ionicons name="stats-chart" size={20} color={colors.primary} />
                </View>
                <View style={styles.scoreboardInfo}>
                  <Text style={[styles.scoreboardTitle, { color: colors.text }]}>Scorebord</Text>
                  <Text style={[styles.scoreboardSubtitle, { color: colors.textSecondary }]}>
                    Bekijk de volledige ranglijst
                  </Text>
                </View>
                <Ionicons name="chevron-forward" size={20} color={colors.textTertiary} />
              </TouchableOpacity>
            </View>
          </View>
        )}

        {/* How to Play */}
        <View style={styles.section}>
          <Text style={[styles.sectionTitle, { color: colors.text }]}>
            Hoe te spelen
          </Text>
          <View style={[styles.rulesCard, { backgroundColor: colors.surface }]}>
            <Text style={[styles.ruleText, { color: colors.textSecondary }]}>
              🎯 Krijg drie symbolen op een rij (horizontaal, verticaal, of
              diagonaal) om te winnen!
            </Text>
            <Text style={[styles.ruleText, { color: colors.textSecondary }]}>
              ❌ Jij speelt met X, de tegenstander met O
            </Text>
            <Text style={[styles.ruleText, { color: colors.textSecondary }]}>
              ⏱️ X begint altijd als eerste
            </Text>
          </View>
        </View>
      </ScrollView>

      {/* Loading overlay */}
      {isLoading && (
        <View style={styles.loadingOverlay}>
          <ActivityIndicator size="large" color="#8B5CF6" />
          <Text style={styles.loadingText}>Spel starten...</Text>
        </View>
      )}

      {/* Challenge Modal */}
      <Modal
        visible={showChallengeModal}
        transparent
        animationType="slide"
        onRequestClose={() => setShowChallengeModal(false)}
      >
        <KeyboardAvoidingView
          style={styles.keyboardAvoidingView}
          behavior={Platform.OS === "ios" ? "padding" : "height"}
          keyboardVerticalOffset={0}
        >
          <View style={styles.modalBackdrop}>
            <View style={styles.modalContainer}>
              <View style={styles.modalHeader}>
                <Text style={styles.modalTitle}>Kies een tegenstander</Text>
              <TouchableOpacity
                onPress={() => setShowChallengeModal(false)}
                style={styles.modalCloseButton}
              >
                <Ionicons name="close" size={24} color="#9CA3AF" />
              </TouchableOpacity>
            </View>

            {/* Series Selector */}
            <View style={styles.seriesSelector}>
              <Text style={styles.seriesSelectorLabel}>Wedstrijdtype</Text>
              <View style={styles.seriesOptions}>
                <TouchableOpacity
                  style={[
                    styles.seriesOption,
                    selectedSeries === "single" && styles.seriesOptionSelected,
                  ]}
                  onPress={() => setSelectedSeries("single")}
                >
                  <Text
                    style={[
                      styles.seriesOptionText,
                      selectedSeries === "single" && styles.seriesOptionTextSelected,
                    ]}
                  >
                    1 spel
                  </Text>
                </TouchableOpacity>
                <TouchableOpacity
                  style={[
                    styles.seriesOption,
                    selectedSeries === "best_of_3" && styles.seriesOptionSelected,
                  ]}
                  onPress={() => setSelectedSeries("best_of_3")}
                >
                  <Text
                    style={[
                      styles.seriesOptionText,
                      selectedSeries === "best_of_3" && styles.seriesOptionTextSelected,
                    ]}
                  >
                    Best of 3
                  </Text>
                </TouchableOpacity>
                <TouchableOpacity
                  style={[
                    styles.seriesOption,
                    selectedSeries === "best_of_5" && styles.seriesOptionSelected,
                  ]}
                  onPress={() => setSelectedSeries("best_of_5")}
                >
                  <Text
                    style={[
                      styles.seriesOptionText,
                      selectedSeries === "best_of_5" && styles.seriesOptionTextSelected,
                    ]}
                  >
                    Best of 5
                  </Text>
                </TouchableOpacity>
              </View>
            </View>

            {/* Search bar */}
            <View style={styles.searchContainer}>
              <Ionicons name="search" size={20} color="#9CA3AF" />
              <TextInput
                style={styles.searchInput}
                placeholder="Zoek op naam..."
                placeholderTextColor="#6B7280"
                value={searchQuery}
                onChangeText={setSearchQuery}
                autoCapitalize="none"
                autoCorrect={false}
              />
              {searchQuery.length > 0 && (
                <TouchableOpacity onPress={() => setSearchQuery("")}>
                  <Ionicons name="close-circle" size={20} color="#9CA3AF" />
                </TouchableOpacity>
              )}
            </View>

            {/* Weekend mode info */}
            {selectedMode === "weekend" && (
              <View style={styles.weekendInfo}>
                <Ionicons name="information-circle" size={16} color="#F59E0B" />
                <Text style={styles.weekendInfoText}>
                  Je kunt alleen spelers van andere teams uitdagen
                </Text>
              </View>
            )}

            {loadingMembers ? (
              <View style={styles.modalLoading}>
                <ActivityIndicator size="large" color="#8B5CF6" />
                <Text style={styles.modalLoadingText}>Laden...</Text>
              </View>
            ) : filteredMembers.length === 0 ? (
              <View style={styles.modalEmpty}>
                <Ionicons name="people-outline" size={48} color="#6B7280" />
                <Text style={styles.modalEmptyText}>
                  {searchQuery
                    ? "Geen resultaten gevonden"
                    : selectedMode === "weekend" 
                    ? "Geen spelers van andere teams gevonden" 
                    : "Geen familieleden gevonden"}
                </Text>
              </View>
            ) : (
              <FlatList
                data={filteredMembers}
                keyExtractor={(item) => item.id}
                renderItem={({ item }) => (
                  <TouchableOpacity
                    style={styles.memberItem}
                    onPress={() => handleChallengeUser(item)}
                  >
                    {item.avatar_url ? (
                      <Image
                        source={{ uri: item.avatar_url }}
                        style={styles.memberAvatar}
                      />
                    ) : (
                      <View style={styles.memberAvatarPlaceholder}>
                        <Text style={styles.memberAvatarText}>
                          {item.full_name?.charAt(0) || "?"}
                        </Text>
                      </View>
                    )}
                    <View style={styles.memberInfo}>
                      <Text style={styles.memberName}>{item.full_name}</Text>
                      {item.team_name && (
                        <Text style={styles.memberTeam}>{item.team_name}</Text>
                      )}
                    </View>
                    <View style={styles.challengeButton}>
                      <Ionicons name="game-controller" size={18} color="#FFFFFF" />
                    </View>
                  </TouchableOpacity>
                )}
                  contentContainerStyle={styles.memberList}
                />
              )}
            </View>
          </View>
        </KeyboardAvoidingView>
      </Modal>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  header: {
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: "rgba(255, 255, 255, 0.1)",
  },
  backButton: {
    padding: 8,
    marginRight: 8,
  },
  headerContent: {
    flex: 1,
  },
  title: {
    fontSize: 24,
    fontWeight: "bold",
  },
  subtitle: {
    fontSize: 14,
    marginTop: 2,
  },
  scrollView: {
    flex: 1,
  },
  content: {
    padding: 16,
    gap: 24,
  },
  section: {
    gap: 12,
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: "600",
  },
  gameOptions: {
    flexDirection: "row",
    gap: 12,
  },
  gameCard: {
    flex: 1,
    padding: 20,
    borderRadius: 16,
    alignItems: "center",
    gap: 8,
  },
  cardTitle: {
    fontSize: 18,
    fontWeight: "600",
  },
  cardSubtitle: {
    fontSize: 12,
    textAlign: "center",
  },
  statsCard: {
    flexDirection: "row",
    borderRadius: 16,
    padding: 16,
    justifyContent: "space-around",
  },
  statItem: {
    alignItems: "center",
    flex: 1,
  },
  statValue: {
    fontSize: 24,
    fontWeight: "bold",
    color: "#FFFFFF",
  },
  statLabel: {
    fontSize: 12,
    marginTop: 4,
  },
  statDivider: {
    width: 1,
    backgroundColor: "rgba(255, 255, 255, 0.1)",
  },
  rulesCard: {
    borderRadius: 16,
    padding: 16,
    gap: 12,
  },
  ruleText: {
    fontSize: 14,
    lineHeight: 20,
  },
  loadingOverlay: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: "rgba(0, 0, 0, 0.7)",
    justifyContent: "center",
    alignItems: "center",
    gap: 12,
  },
  loadingText: {
    color: "#FFFFFF",
    fontSize: 16,
  },
  // Modal styles
  keyboardAvoidingView: {
    flex: 1,
  },
  modalBackdrop: {
    flex: 1,
    backgroundColor: "rgba(0, 0, 0, 0.7)",
    justifyContent: "flex-end",
  },
  modalContainer: {
    backgroundColor: "#1F2937",
    borderTopLeftRadius: 24,
    borderTopRightRadius: 24,
    maxHeight: "70%",
  },
  modalHeader: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    padding: 16,
    borderBottomWidth: 1,
    borderBottomColor: "rgba(255, 255, 255, 0.1)",
  },
  modalTitle: {
    fontSize: 18,
    fontWeight: "600",
    color: "#FFFFFF",
  },
  modalCloseButton: {
    padding: 4,
  },
  modalLoading: {
    padding: 48,
    alignItems: "center",
    gap: 12,
  },
  modalLoadingText: {
    color: "#9CA3AF",
    fontSize: 14,
  },
  modalEmpty: {
    padding: 48,
    alignItems: "center",
    gap: 12,
  },
  modalEmptyText: {
    color: "#9CA3AF",
    fontSize: 14,
  },
  memberList: {
    padding: 8,
  },
  memberItem: {
    flexDirection: "row",
    alignItems: "center",
    padding: 12,
    borderRadius: 12,
    marginBottom: 8,
    backgroundColor: "#374151",
  },
  memberAvatar: {
    width: 48,
    height: 48,
    borderRadius: 24,
  },
  memberAvatarPlaceholder: {
    width: 48,
    height: 48,
    borderRadius: 24,
    backgroundColor: "#4B5563",
    justifyContent: "center",
    alignItems: "center",
  },
  memberAvatarText: {
    fontSize: 18,
    fontWeight: "bold",
    color: "#FFFFFF",
  },
  memberInfo: {
    flex: 1,
    marginLeft: 12,
  },
  memberName: {
    fontSize: 16,
    fontWeight: "500",
    color: "#FFFFFF",
  },
  memberTeam: {
    fontSize: 12,
    color: "#9CA3AF",
    marginTop: 2,
  },
  challengeButton: {
    width: 36,
    height: 36,
    borderRadius: 18,
    backgroundColor: "#8B5CF6",
    justifyContent: "center",
    alignItems: "center",
  },
  // Series selector styles
  seriesSelector: {
    paddingHorizontal: 16,
    paddingTop: 16,
  },
  seriesSelectorLabel: {
    fontSize: 14,
    fontWeight: "500",
    color: "#9CA3AF",
    marginBottom: 8,
  },
  seriesOptions: {
    flexDirection: "row",
    gap: 8,
  },
  seriesOption: {
    flex: 1,
    paddingVertical: 10,
    borderRadius: 8,
    backgroundColor: "#374151",
    alignItems: "center",
  },
  seriesOptionSelected: {
    backgroundColor: "#8B5CF6",
  },
  seriesOptionText: {
    fontSize: 14,
    fontWeight: "500",
    color: "#9CA3AF",
  },
  seriesOptionTextSelected: {
    color: "#FFFFFF",
  },
  searchContainer: {
    flexDirection: "row",
    alignItems: "center",
    marginHorizontal: 16,
    marginTop: 12,
    paddingHorizontal: 12,
    paddingVertical: 10,
    backgroundColor: "rgba(128, 128, 128, 0.2)",
    borderRadius: 12,
    gap: 8,
  },
  searchInput: {
    flex: 1,
    fontSize: 16,
    paddingVertical: 0,
    color: "#FFFFFF",
  },
  weekendInfo: {
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 16,
    paddingTop: 12,
    gap: 8,
  },
  weekendInfoText: {
    fontSize: 12,
    color: "#F59E0B",
    flex: 1,
  },
  // Weekend points card
  weekendPointsCard: {
    flexDirection: "row",
    alignItems: "center",
    padding: 16,
    borderRadius: 12,
    gap: 12,
  },
  weekendPointsContent: {
    flex: 1,
  },
  weekendPointsTitle: {
    fontSize: 14,
    fontWeight: "600",
    color: "#FFFFFF",
  },
  weekendPointsValue: {
    fontSize: 12,
    color: "rgba(255, 255, 255, 0.8)",
  },
  // Leaderboard styles
  leaderboardCard: {
    borderRadius: 16,
    overflow: "hidden",
  },
  leaderboardItem: {
    flexDirection: "row",
    alignItems: "center",
    padding: 12,
    gap: 12,
  },
  leaderboardRank: {
    width: 28,
    alignItems: "center",
  },
  rankEmoji: {
    fontSize: 20,
  },
  rankNumber: {
    fontSize: 14,
    fontWeight: "600",
  },
  leaderboardAvatar: {
    width: 40,
    height: 40,
    borderRadius: 20,
  },
  leaderboardAvatarPlaceholder: {
    width: 40,
    height: 40,
    borderRadius: 20,
    justifyContent: "center",
    alignItems: "center",
  },
  avatarText: {
    fontSize: 16,
    fontWeight: "600",
  },
  leaderboardInfo: {
    flex: 1,
  },
  leaderboardName: {
    fontSize: 14,
    fontWeight: "500",
  },
  youBadge: {
    fontSize: 12,
    fontWeight: "600",
  },
  leaderboardStats: {
    fontSize: 12,
    marginTop: 2,
  },
  leaderboardPoints: {
    fontSize: 16,
    fontWeight: "700",
  },
  scoreboardLink: {
    flexDirection: "row",
    alignItems: "center",
    padding: 14,
    gap: 12,
  },
  scoreboardIcon: {
    width: 40,
    height: 40,
    borderRadius: 12,
    justifyContent: "center",
    alignItems: "center",
  },
  scoreboardInfo: {
    flex: 1,
  },
  scoreboardTitle: {
    fontSize: 14,
    fontWeight: "500",
  },
  scoreboardSubtitle: {
    fontSize: 12,
    marginTop: 2,
  },
});

